/**
 * Day-14 metric, in-app signal: log `briefing_view` once per user per UTC day, when the
 * stored briefing card actually renders (not on route load). Dedupe is client-side
 * (localStorage, which can throw or be absent: then we simply log again, never fail).
 * Skipped while a founder impersonates a partner, or the metric would count Nick's views.
 */
import { useEffect } from 'react';
import { api } from './api';
import { useMe } from './hooks/use-me';
import type { MorningBriefing } from './morning-briefing';

type Store = Pick<Storage, 'getItem' | 'setItem'>;

export async function logBriefingViewOnce(opts: {
  store: Store | null;
  userId: string;
  date: string;
  surface: 'web' | 'mobile';
  status?: string;
  post: (body: Record<string, unknown>) => Promise<unknown>;
}): Promise<boolean> {
  const key = `wp:briefing-view:${opts.userId}:${opts.date}`;
  try { if (opts.store?.getItem(key)) return false; } catch { /* storage unavailable: log anyway */ }
  try { opts.store?.setItem(key, '1'); } catch { /* ignore */ }
  try {
    await opts.post({
      event_type: 'briefing_view',
      entity_type: 'briefing',
      entity_id: opts.date,
      event_data: { surface: opts.surface, status: opts.status },
    });
  } catch { /* best-effort: analytics never breaks the page */ }
  return true;
}

/** Call from the component that renders the briefing. `data` is the /v1/morning-briefing body. */
export function useLogBriefingView(data: Pick<MorningBriefing, 'status' | 'source'> | undefined, surface: 'web' | 'mobile'): void {
  const me = useMe();
  const userId = me.user?.id; // '' / absent until /v1/me loads
  const real = !!data?.source; // the API-failure fallback has no `source`
  useEffect(() => {
    if (!real || !userId || me.impersonating) return;
    let store: Store | null = null;
    try { store = window.localStorage; } catch { /* blocked */ }
    void logBriefingViewOnce({
      store, userId, surface, status: data?.status,
      date: new Date().toISOString().slice(0, 10),
      post: (body) => api('/v1/interactions', { method: 'POST', body: JSON.stringify(body) }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [real, userId]);
}
